import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  Grid,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  AddCircleOutline as AddCircleOutlineIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import MDDatePicker from 'components/MDDatePicker';
import MDTimePicker from 'components/MDTimePicker';
import { api } from 'api';
import { usePermissions } from 'hooks/usePermissions';

const EmbroTransferModal = ({ open, onClose, procedureId, visitId, contactId, primaryColor = '#00798E' }) => {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('v1-embro-transfer-create') || hasPermission('v1-ivf-create-procedure');
  const canEdit = hasPermission('v1-embro-transfer-update') || hasPermission('v1-ivf-update-procedure');
  const canDelete = hasPermission('v1-embro-transfer-delete') || hasPermission('v1-ivf-delete-procedure');

  // View management
  const [view, setView] = useState('list'); // 'list', 'create', 'details'
  const [transfers, setTransfers] = useState([]);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [paramMembers, setParamMembers] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editingVisitId, setEditingVisitId] = useState(visitId || null);
  const [editingContactId, setEditingContactId] = useState(contactId || null);

  // Transfer Data Form
  const [transferForm, setTransferForm] = useState({
    embro_date: '',
    embro_time: '',
    embro_duration: '',
    surgeon: '',
    embrologist: '',
    anasthetist: '',
    luteal_phase_support: '',
    no_of_embryos_transferred: '',
    no_of_embryos_frozen: '',
  });

  // Transfer Details Form
  const [detailForm, setDetailForm] = useState({
    anasthesia: '',
    position: '',
    vaginal_discharge: '',
    cervix: '',
    volsellum_used: '',
    dilator: '',
    transfer: '',
    type_of_catheter: '',
    mucus_on_catheter: '',
    blood_on_catheter: '',
    spares: '',
    any_abnormalities: '',
    reinsertion_of_catheter: '',
    double_et: '',
    comment: '',
  });

  // Embryos
  const [embros, setEmbros] = useState([{ number: '', cells: '', grade: '' }]);
  const [errors, setErrors] = useState({});

  // Load transfers
  const loadTransfers = async () => {
    try {
      setLoading(true);
      const res = await api.embroTransfer.getEmbroTransfersByProcedure(procedureId);
      setTransfers(res.data || []);
    } catch (err) {
      console.error('Error loading transfers:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load embryo transfers' });
    } finally {
      setLoading(false);
    }
  };

  // Load doctors and parameters
  const loadDoctorsAndParams = async () => {
    try {
      const doctorsRes = await api.contacts.getDoctorsList();
      setDoctors(doctorsRes.data || []);

      try {
        const paramsRes = await api.ivf.getEmbroTransferParamGroup();
        if (paramsRes?.data?.paramMembers) {
          const membersMap = {};
          paramsRes.data.paramMembers.forEach(member => {
            membersMap[member.slug] = member.options || [];
          });
          setParamMembers(membersMap);
        }
      } catch (paramErr) {
        console.log('Could not load embryo transfer params:', paramErr.message);
        setParamMembers({});
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  useEffect(() => {
    if (open && view === 'list') {
      loadTransfers();
      loadDoctorsAndParams();
    }
  }, [open, view]);

  // Reset forms
  const resetForms = () => {
    setTransferForm({
      embro_date: '',
      embro_time: '',
      embro_duration: '',
      surgeon: '',
      embrologist: '',
      anasthetist: '',
      luteal_phase_support: '',
      no_of_embryos_transferred: '',
      no_of_embryos_frozen: '',
    });
    setDetailForm({
      anasthesia: '',
      position: '',
      vaginal_discharge: '',
      cervix: '',
      volsellum_used: '',
      dilator: '',
      transfer: '',
      type_of_catheter: '',
      mucus_on_catheter: '',
      blood_on_catheter: '',
      spares: '',
      any_abnormalities: '',
      reinsertion_of_catheter: '',
      double_et: '',
      comment: '',
    });
    setEmbros([{ number: '', cells: '', grade: '' }]);
    setErrors({});
    setEditingId(null);
  };

  // Validate transfer form
  const validateTransferForm = () => {
    const newErrors = {};
    if (!transferForm.embro_date) newErrors['embro_date'] = 'Date is required';
    if (!transferForm.embro_time) newErrors['embro_time'] = 'Time is required';
    if (!transferForm.surgeon) newErrors['surgeon'] = 'Surgeon is required';
    if (!transferForm.embrologist) newErrors['embrologist'] = 'Embryologist is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle create transfer
  const handleCreateTransfer = async () => {
    if (!validateTransferForm()) {
      Swal.fire({ icon: 'warning', title: 'Validation Error', text: 'Please check all required fields' });
      return;
    }

    setSaving(true);
    try {
      await api.embroTransfer.createEmbroTransfer(procedureId, {
        irm_visits_id: visitId || null,
        irm_contacts_id: contactId || null,
        transfer: transferForm,
        detail: detailForm,
        embros: embros.filter(e => e.number),
      });
      Swal.fire({ icon: 'success', title: 'Created', text: 'Embryo transfer created successfully', timer: 1500, showConfirmButton: false });
      resetForms();
      await loadTransfers();
      setView('list');
    } catch (err) {
      console.error('Error saving:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to save embryo transfer' });
    } finally {
      setSaving(false);
    }
  };

  // Handle update transfer (from details view)
  const handleUpdateTransfer = async () => {
    setSaving(true);
    try {
      const updatePayload = {
        transfer: transferForm,
        detail: detailForm,
        irm_visits_id: editingVisitId || null,
        irm_contacts_id: editingContactId || null,
      };
      await api.embroTransfer.updateEmbroTransfer(editingId, updatePayload);
      const embrosToUpdate = embros.filter(e => e.number).map(({ id, ...rest }) => rest);
      await api.embroTransfer.updateTransferredEmbros(editingId, embrosToUpdate);
      Swal.fire({ icon: 'success', title: 'Updated', text: 'Embryo transfer updated successfully', timer: 1500, showConfirmButton: false });
      resetForms();
      await loadTransfers();
      setView('list');
    } catch (err) {
      console.error('Error updating:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to update embryo transfer' });
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (transferId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Embryo Transfer?',
      text: 'This will delete the transfer and all related data. Cannot be undone.',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      confirmButtonText: 'Delete',
    });

    if (!result.isConfirmed) return;

    try {
      await api.embroTransfer.deleteEmbroTransfer(transferId);
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1500, showConfirmButton: false });
      await loadTransfers();
    } catch (err) {
      console.error('Error deleting:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete embryo transfer' });
    }
  };

  // Handle edit (load transfer data into details view)
  const handleEdit = async (transfer) => {
    try {
      setLoading(true);
      const res = await api.embroTransfer.getEmbroTransfer(transfer.transfer?.id);
      const { transfer: transferData, detail, embros: embroData } = res.data;

      setTransferForm({
        embro_date: transferData?.embro_date || '',
        embro_time: transferData?.embro_time || '',
        embro_duration: transferData?.embro_duration || '',
        surgeon: transferData?.surgeon || '',
        embrologist: transferData?.embrologist || '',
        anasthetist: transferData?.anasthetist || '',
        luteal_phase_support: transferData?.luteal_phase_support || '',
        no_of_embryos_transferred: transferData?.no_of_embryos_transferred ?? '',
        no_of_embryos_frozen: transferData?.no_of_embryos_frozen ?? '',
      });

      setDetailForm(detail || {
        anasthesia: '',
        position: '',
        vaginal_discharge: '',
        cervix: '',
        volsellum_used: '',
        dilator: '',
        transfer: '',
        type_of_catheter: '',
        mucus_on_catheter: '',
        blood_on_catheter: '',
        spares: '',
        any_abnormalities: '',
        reinsertion_of_catheter: '',
        double_et: '',
        comment: '',
      });

      setEmbros(embroData && embroData.length > 0 ? embroData : [{ number: '', cells: '', grade: '' }]);
      setEditingId(transfer.transfer?.id);
      setEditingVisitId(transferData?.irm_visits_id || visitId || null);
      setEditingContactId(transferData?.irm_contacts_id || contactId || null);
      setSelectedTransfer(transfer);
      setView('details');
    } catch (err) {
      console.error('Error loading transfer:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load embryo transfer' });
    } finally {
      setLoading(false);
    }
  };

  // Embryo row handlers
  const addEmbroRow = () => {
    setEmbros([...embros, { number: '', cells: '', grade: '' }]);
  };

  const removeEmbroRow = (index) => {
    setEmbros(embros.filter((_, i) => i !== index));
  };

  const updateEmbroRow = (index, field, value) => {
    setEmbros(embros.map((e, i) => i === index ? { ...e, [field]: value } : e));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* HEADER */}
      <Box sx={{
        backgroundColor: primaryColor,
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 3,
        py: 2,
        fontWeight: 600,
        fontSize: '1.2rem'
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          {view !== 'list' && (
            <IconButton
              size="small"
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
              onClick={() => {
                setView('list');
                resetForms();
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <span>Embryo Transfer Data</span>
        </Box>
        <IconButton
          size="small"
          sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* CONTENT */}
      <Box sx={{ px: 3, py: 2.5 }}>
        {/* LIST VIEW */}
        {view === 'list' && (
          <Box>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForms();
                  setView('create');
                }}
                sx={{
                  mb: 2,
                  backgroundColor: primaryColor,
                  fontWeight: 600,
                  color: 'white !important',
                  align: 'right',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: primaryColor,
                    opacity: 0.9
                  }
                }}
              >
                Create New Transfer
              </Button>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress sx={{ color: primaryColor }} />
              </Box>
            ) : transfers.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 6,
                  px: 3,
                  backgroundColor: `${primaryColor}08`,
                  borderRadius: 2,
                  border: `2px dashed ${primaryColor}40`,
                }}
              >
                <AddCircleOutlineIcon
                  sx={{
                    fontSize: 80,
                    color: primaryColor,
                    opacity: 0.3,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 600, mb: 1 }}>
                  No Embryo Transfer Records Yet
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 3, textAlign: 'center' }}>
                  Start tracking embryo transfers by creating your first record
                </Typography>
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      resetForms();
                      setView('create');
                    }}
                    sx={{
                      backgroundColor: primaryColor,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: primaryColor,
                        opacity: 0.9,
                      },
                    }}
                  >
                    Create First Transfer
                  </Button>
                )}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ backgroundColor: `${primaryColor}15` }}>
                      <TableCell sx={{ fontWeight: 700, color: primaryColor }}><strong>Date</strong></TableCell>
                      <TableCell sx={{ fontWeight: 700, color: primaryColor }}><strong>Time</strong></TableCell>
                      <TableCell sx={{ fontWeight: 700, color: primaryColor }}><strong>Surgeon</strong></TableCell>
                      <TableCell sx={{ fontWeight: 700, color: primaryColor }}><strong>Embryologist</strong></TableCell>
                      <TableCell sx={{ fontWeight: 700, color: primaryColor }} align="center"><strong>Embryos</strong></TableCell>
                      <TableCell sx={{ fontWeight: 700, color: primaryColor }} align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                    {transfers.map((transfer) => (
                      <TableRow key={transfer.transfer?.id} hover sx={{ '&:hover': { backgroundColor: `${primaryColor}08` } }}>
                        <TableCell>{transfer.transfer?.embro_date ? new Date(transfer.transfer.embro_date).toLocaleDateString('en-GB') : '—'}</TableCell>
                        <TableCell>{transfer.transfer?.embro_time || '—'}</TableCell>
                        <TableCell>{transfer.transfer?.surgeon || '—'}</TableCell>
                        <TableCell>{transfer.transfer?.embrologist || '—'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${(transfer.embros || []).length} embryos`}
                            size="small"
                            sx={{
                              backgroundColor: `${primaryColor}20`,
                              color: primaryColor,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {canEdit && (
                            <Tooltip title="View/Edit Details">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(transfer)}
                                sx={{ color: primaryColor, '&:hover': { backgroundColor: `${primaryColor}15` } }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete Transfer">
                              <IconButton
                                size="small"
                                sx={{ color: '#d32f2f', '&:hover': { backgroundColor: '#d32f2f15' } }}
                                onClick={() => handleDelete(transfer.transfer?.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* CREATE VIEW */}
        {view === 'create' && (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: primaryColor }}>
              Create New Embryo Transfer Record
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  label="Transfer Date *"
                  value={transferForm.embro_date}
                  onChange={(date) => setTransferForm({ ...transferForm, embro_date: date })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTimePicker
                  label="Transfer Time *"
                  value={transferForm.embro_time}
                  onChange={(time) => setTransferForm({ ...transferForm, embro_time: time })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (mins)"
                  type="number"
                  value={transferForm.embro_duration}
                  onChange={(e) => setTransferForm({ ...transferForm, embro_duration: e.target.value })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: primaryColor },
                      '&.Mui-focused fieldset': { borderColor: primaryColor },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: primaryColor } }}>Surgeon *</InputLabel>
                  <Select
                    value={transferForm.surgeon}
                    label="Surgeon *"
                    onChange={(e) => setTransferForm({ ...transferForm, surgeon: e.target.value })}
                    sx={{
                      height: '40px',
                      minHeight: '40px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: primaryColor },
                        '&.Mui-focused fieldset': { borderColor: primaryColor },
                      },
                    }}
                  >
                    {doctors.map(doc => (
                      <MenuItem key={doc.user_id} value={doc.name}>{doc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: primaryColor } }}>Embryologist *</InputLabel>
                  <Select
                    value={transferForm.embrologist}
                    label="Embryologist *"
                    onChange={(e) => setTransferForm({ ...transferForm, embrologist: e.target.value })}
                    sx={{
                      height: '40px',
                      minHeight: '40px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: primaryColor },
                        '&.Mui-focused fieldset': { borderColor: primaryColor },
                      },
                    }}
                  >
                    {doctors.map(doc => (
                      <MenuItem key={doc.user_id} value={doc.name}>{doc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Anaesthetist"
                  value={transferForm.anasthetist}
                  onChange={(e) => setTransferForm({ ...transferForm, anasthetist: e.target.value })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: primaryColor },
                      '&.Mui-focused fieldset': { borderColor: primaryColor },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Advice"
                  multiline
                  rows={2}
                  value={transferForm.luteal_phase_support}
                  onChange={(e) => setTransferForm({ ...transferForm, luteal_phase_support: e.target.value })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: primaryColor },
                      '&.Mui-focused fieldset': { borderColor: primaryColor },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Embryos Transferred"
                  type="number"
                  value={transferForm.no_of_embryos_transferred}
                  onChange={(e) => setTransferForm({ ...transferForm, no_of_embryos_transferred: e.target.value })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: primaryColor },
                      '&.Mui-focused fieldset': { borderColor: primaryColor },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Embryos Frozen"
                  type="number"
                  value={transferForm.no_of_embryos_frozen}
                  onChange={(e) => setTransferForm({ ...transferForm, no_of_embryos_frozen: e.target.value })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: primaryColor },
                      '&.Mui-focused fieldset': { borderColor: primaryColor },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* DETAILS VIEW */}
        {view === 'details' && selectedTransfer && (
          <Box>
            <Card sx={{
              mb: 3,
              backgroundColor: `${primaryColor}12`,
              border: `2px solid ${primaryColor}30`
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: primaryColor }}>
                  📋 Transfer Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Date:</strong> {selectedTransfer.transfer?.embro_date ? new Date(selectedTransfer.transfer.embro_date).toLocaleDateString('en-GB') : '—'}</Typography>
                    <Typography variant="body2"><strong>Time:</strong> {selectedTransfer.transfer?.embro_time || '—'}</Typography>
                    <Typography variant="body2"><strong>Duration:</strong> {selectedTransfer.transfer?.embro_duration || '—'} mins</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Surgeon:</strong> {selectedTransfer.transfer?.surgeon || '—'}</Typography>
                    <Typography variant="body2"><strong>Embryologist:</strong> {selectedTransfer.transfer?.embrologist || '—'}</Typography>
                    <Typography variant="body2"><strong>Anaesthetist:</strong> {selectedTransfer.transfer?.anasthetist || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Embryos Transferred:</strong> {selectedTransfer.transfer?.no_of_embryos_transferred ?? '—'}</Typography>
                    <Typography variant="body2"><strong>Embryos Frozen:</strong> {selectedTransfer.transfer?.no_of_embryos_frozen ?? '—'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: primaryColor }}>
              📝 Transfer Details Form
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { key: 'anasthesia', label: 'ANESTHESIA', slug: 'embro-transfer-anesthesia' },
                { key: 'position', label: 'POSITION', slug: 'embro-transfer-position' },
                { key: 'vaginal_discharge', label: 'VAGINAL DISCHARGE', slug: 'embro-transfer-vaginal-discharge' },
                { key: 'cervix', label: 'CERVIX', slug: 'embro-transfer-cervix' },
                { key: 'volsellum_used', label: 'VOLSELLUM USED', slug: 'embro-transfer-volsellum' },
                { key: 'dilator', label: 'DILATOR', slug: 'embro-transfer-dilator' },
                { key: 'transfer', label: 'TRANSFER DIFFICULTY', slug: 'embro-transfer-difficulty' },
                { key: 'type_of_catheter', label: 'TYPE OF CATHETER', slug: 'embro-transfer-catheter' },
                { key: 'mucus_on_catheter', label: 'MUCUS ON CATHETER', slug: 'embro-transfer-mucus' },
                { key: 'blood_on_catheter', label: 'BLOOD ON CATHETER', slug: 'embro-transfer-blood' },
              ].map(field => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ '&.Mui-focused': { color: primaryColor } }}>{field.label}</InputLabel>
                    <Select
                      value={detailForm[field.key]}
                      label={field.label}
                      onChange={(e) => setDetailForm({ ...detailForm, [field.key]: e.target.value })}
                      sx={{
                        height: '40px',
                        minHeight: '40px',
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: primaryColor },
                          '&.Mui-focused fieldset': { borderColor: primaryColor },
                        },
                      }}
                    >
                      {(paramMembers[field.slug] || []).map(option => (
                        <MenuItem key={option.id} value={option.value.toUpperCase()}>{option.value.toUpperCase()}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Spares"
                  multiline
                  rows={2}
                  value={detailForm.spares}
                  onChange={(e) => setDetailForm({ ...detailForm, spares: e.target.value })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: primaryColor },
                      '&.Mui-focused fieldset': { borderColor: primaryColor },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comment"
                  multiline
                  rows={3}
                  value={detailForm.comment}
                  onChange={(e) => setDetailForm({ ...detailForm, comment: e.target.value })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: primaryColor },
                      '&.Mui-focused fieldset': { borderColor: primaryColor },
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: primaryColor }}>
              🧬 Transferred Embryos
            </Typography>

            <Card sx={{
              mb: 3,
              border: `2px solid ${primaryColor}`,
              backgroundColor: `${primaryColor}05`
            }}>
              <CardContent>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: primaryColor }}>
                    Embryo List
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addEmbroRow}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      color: primaryColor,
                      '&:hover': { backgroundColor: `${primaryColor}15` }
                    }}
                  >
                    Add Row
                  </Button>
                </Box>

                {embros.length > 0 ? (
                  <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                    <Table size="small">
                      <TableBody>
                         <TableRow sx={{ backgroundColor: `${primaryColor}15` }}>
                          <TableCell sx={{ fontWeight: 700, color: primaryColor }}><strong>Number</strong></TableCell>
                          <TableCell sx={{ fontWeight: 700, color: primaryColor }}><strong>Cells</strong></TableCell>
                          <TableCell sx={{ fontWeight: 700, color: primaryColor }}><strong>Grade</strong></TableCell>
                          <TableCell sx={{ fontWeight: 700, color: primaryColor }} align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                        {embros.map((embro, idx) => (
                          <TableRow key={idx} hover sx={{ '&:hover': { backgroundColor: `${primaryColor}08` } }}>
                            <TableCell>
                              <TextField
                                size="small"
                                value={embro.number}
                                onChange={(e) => updateEmbroRow(idx, 'number', e.target.value)}
                                variant="outlined"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': { borderColor: primaryColor },
                                    '&.Mui-focused fieldset': { borderColor: primaryColor },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                value={embro.cells}
                                onChange={(e) => updateEmbroRow(idx, 'cells', e.target.value)}
                                variant="outlined"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': { borderColor: primaryColor },
                                    '&.Mui-focused fieldset': { borderColor: primaryColor },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                value={embro.grade}
                                onChange={(e) => updateEmbroRow(idx, 'grade', e.target.value)}
                                variant="outlined"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': { borderColor: primaryColor },
                                    '&.Mui-focused fieldset': { borderColor: primaryColor },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Delete Embryo">
                                <IconButton
                                  size="small"
                                  onClick={() => removeEmbroRow(idx)}
                                  sx={{ color: '#d32f2f', '&:hover': { backgroundColor: '#d32f2f15' } }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">No embryos added yet. Click &quot;Add Row&quot; to add embryos.</Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {/* ACTIONS */}
      <Box sx={{ p: 2, gap: 1, display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid #e0e0e0` }}>
        {view === 'create' && (
          <>
            <Button
              onClick={() => {
                setView('list');
                resetForms();
              }}
              sx={{ color: '#666', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateTransfer}
              disabled={saving}
              sx={{
                backgroundColor: primaryColor,
                fontWeight: 600,
                color: 'white !important',
                '&:hover': { backgroundColor: primaryColor, opacity: 0.9 }
              }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Create Transfer'}
            </Button>
          </>
        )}
        {view === 'details' && (
          <>
            <Button
              onClick={() => {
                setView('list');
                resetForms();
              }}
              sx={{ color: primaryColor, fontWeight: 600 }}
            >
              Back to List
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateTransfer}
              disabled={saving}
              sx={{
                backgroundColor: primaryColor,
                fontWeight: 600,
                color: 'white !important',
                '&:hover': { backgroundColor: primaryColor, opacity: 0.9 }
              }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Update Transfer'}
            </Button>
          </>
        )}
        {view === 'list' && (
          <Button
            onClick={onClose}
            sx={{
              fontWeight: 600,
              color: primaryColor,
              '&:hover': { backgroundColor: `${primaryColor}10` }
            }}
          >
            Close
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

EmbroTransferModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  procedureId: PropTypes.number.isRequired,
  visitId: PropTypes.number,
  contactId: PropTypes.number,
  primaryColor: PropTypes.string,
};

EmbroTransferModal.defaultProps = {
  visitId: null,
  contactId: null,
  primaryColor: '#00798E',
};

export default EmbroTransferModal;
